// src/pages/Accounting/BankTransactions/utils.ts

import type {
  Transaction,
  Bank,
  TransactionStatus,
  CountryCode,
} from "./types";
import { ALL_BANKS } from "./constants";

export function formatCurrency(amount: number, direction?: "in" | "out"): string {
  const abs = Math.abs(amount);
  const formatted = abs.toLocaleString("en-CA", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
  if (direction === "in") return `+ $${formatted}`;
  if (direction === "out") return `− $${formatted}`;
  return `$${formatted}`;
}

export function formatBalance(amount: number): string {
  return `$${amount.toLocaleString("en-CA", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
}

export function formatDate(iso: string): string {
  const d = new Date(iso);
  return d.toLocaleDateString("en-CA", { month: "short", day: "2-digit" });
}

export function formatSyncedAt(iso: string): string {
  const d = new Date(iso);
  const now = Date.now();
  const diffMin = Math.round((now - d.getTime()) / 60000);
  if (diffMin < 1) return "synced just now";
  if (diffMin < 60) return `synced ${diffMin} min ago`;
  const diffHr = Math.round(diffMin / 60);
  if (diffHr < 24) return `synced ${diffHr} hr ago`;
  return `synced ${Math.round(diffHr / 24)} days ago`;
}

export function filterBanks(banks: Bank[], query: string): Bank[] {
  const q = query.trim().toLowerCase();
  if (!q) return banks;
  return banks.filter(
    (b) =>
      b.name.toLowerCase().includes(q) ||
      b.shortName.toLowerCase().includes(q) ||
      b.code.toLowerCase().includes(q)
  );
}

export function getBanksForCountry(code: CountryCode): Bank[] {
  return ALL_BANKS.filter((b) => b.country === code);
}

export function getPopularBanksForCountry(code: CountryCode): Bank[] {
  return ALL_BANKS.filter((b) => b.country === code && b.isPopular);
}

export function groupTransactionsByStatus(
  transactions: Transaction[]
): Record<TransactionStatus, Transaction[]> {
  return transactions.reduce(
    (acc, tx) => {
      acc[tx.status].push(tx);
      return acc;
    },
    {
      auto: [] as Transaction[],
      review: [] as Transaction[],
      reconciled: [] as Transaction[],
    }
  );
}

export function sumByDirection(
  transactions: Transaction[],
  direction: "in" | "out"
): number {
  return transactions
    .filter((t) => t.direction === direction)
    .reduce((sum, t) => sum + t.amount, 0);
}

export function countByStatus(
  transactions: Transaction[],
  status: TransactionStatus
): number {
  return transactions.filter((t) => t.status === status).length;
}

export function countByDirection(
  transactions: Transaction[],
  direction: "in" | "out"
): number {
  return transactions.filter((t) => t.direction === direction).length;
}